import { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth';

interface AuditLog {
    id: string;
    action: string;
    entity_type: string;
    entity_id?: string;
    actor?: { full_name: string; email: string };
    actor_user_id?: string;
    ip_hash?: string;
    before_data?: Record<string, any>;
    after_data?: Record<string, any>;
    created_at?: string;
}

const actionMeta: Record<string, { label: string; bg: string; text: string }> = {
    'user.login': { label: 'Đăng nhập', bg: 'bg-emerald-50 text-emerald-700 border border-emerald-200', text: '' },
    'campaign.create': { label: 'Tạo chiến dịch', bg: 'bg-blue-50 text-blue-700 border border-blue-200', text: '' },
    'campaign.update': { label: 'Cập nhật', bg: 'bg-amber-50 text-amber-700 border border-amber-200', text: '' },
    'campaign.publish': { label: 'Phát hành', bg: 'bg-indigo-50 text-indigo-700 border border-indigo-200', text: '' },
    'gift.create': { label: 'Tạo quà tặng', bg: 'bg-teal-50 text-teal-700 border border-teal-200', text: '' },
    'gift.update': { label: 'Cập nhật quà', bg: 'bg-orange-50 text-orange-700 border border-orange-200', text: '' },
    'gift.delete': { label: 'Xóa quà', bg: 'bg-rose-50 text-rose-700 border border-rose-200', text: '' },
};

const entityLabels: Record<string, string> = {
    'user': 'Thành viên',
    'campaign': 'Chiến dịch',
    'gift': 'Quà tặng',
};

export default function AuditLogs() {
    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [actionFilter, setActionFilter] = useState('');
    const { user } = useAuthStore();
    const tenantId = localStorage.getItem('impersonated_tenant_id') || user?.tenant_id || '';

    useEffect(() => {
        const fetchLogs = async () => {
            setLoading(true);
            try {
                const params: Record<string, any> = { tenantId, page, limit: 50 };
                if (actionFilter) params.action = actionFilter;
                const data = await api.getAuditLogs(params);
                setLogs(data?.data || data?.[0] || []);
                setTotal(data?.total ?? data?.[1] ?? 0);
            } catch (err) {
                console.error('Failed to fetch audit logs:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchLogs();
    }, [page, tenantId, actionFilter]);

    return (
        <Layout>
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Nhật ký hệ thống</h1>
                    <p className="text-gray-500 mt-1">Theo dõi tất cả hoạt động hệ thống</p>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6">
                <div className="flex gap-4">
                    <select
                        value={actionFilter}
                        onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
                        className="px-4 py-2 border border-gray-300 rounded-lg"
                    >
                        <option value="">Tất cả hành động</option>
                        <option value="campaign.create">Tạo chiến dịch</option>
                        <option value="campaign.update">Cập nhật chiến dịch</option>
                        <option value="campaign.publish">Xuất bản chiến dịch</option>
                        <option value="gift.create">Tạo quà tặng</option>
                        <option value="gift.update">Cập nhật quà tặng</option>
                        <option value="user.login">Đăng nhập</option>
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-64"><div className="text-gray-500">Đang tải...</div></div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Thời gian</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Người thực hiện</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hành động</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Đối tượng</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {logs.map((log) => (
                                <tr key={log.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        {log.created_at ? new Date(log.created_at).toLocaleString() : '-'}
                                    </td>
                                    <td className="px-6 py-4 text-sm">
                                        <div className="font-semibold text-gray-900">
                                            {log.actor?.full_name || 'Hệ thống'}
                                        </div>
                                        {log.actor?.email && (
                                            <div className="text-xs text-gray-400 font-mono mt-0.5">
                                                {log.actor.email}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        {(() => {
                                            const meta = actionMeta[log.action] || { label: log.action, bg: 'bg-gray-50 text-gray-700 border border-gray-200', text: '' };
                                            return (
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${meta.bg}`}>
                                                    {meta.label}
                                                </span>
                                            );
                                        })()}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        <span className="font-semibold text-gray-700">
                                            {entityLabels[log.entity_type] || log.entity_type}
                                        </span>
                                        {log.entity_id ? (
                                            <span className="text-xs text-gray-400 font-mono ml-1.5" title={log.entity_id}>
                                                (#{log.entity_id.substring(0, 8)})
                                            </span>
                                        ) : ''}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500 font-mono">
                                        {log.ip_hash || '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            <div className="flex justify-between items-center mt-4">
                <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50"
                >
                    Trước
                </button>
                <span className="text-sm text-gray-500">Trang {page}</span>
                <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={page * 50 >= total}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50"
                >
                    Tiếp
                </button>
            </div>
        </Layout>
    );
}